import {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import AuthContext from "../../store/auth-context";
import { TicketContext } from "../../store/ticket-context";
import useHttp from "../../hooks/use-http";
import SessionAccordion from "./SessionAccordion";
import { formatShortDate, formatDuration } from "./format";

const PAGE_SIZE = 20;

// First ancestor that actually overflows; falls back to first overflow:auto
// ancestor, then document scroll.
const findScroller = (from) => {
  let candidate = null;
  let el = from && from.parentElement;
  while (el) {
    const oy = window.getComputedStyle(el).overflowY;
    if (oy === "auto" || oy === "scroll") {
      if (el.scrollHeight > el.clientHeight) return el;
      if (!candidate) candidate = el;
    }
    el = el.parentElement;
  }
  return candidate || document.scrollingElement || document.documentElement;
};

const isDocScroller = (el) =>
  el === document.scrollingElement || el === document.documentElement;

const readScroll = (el) =>
  !el ? 0 : isDocScroller(el) ? window.scrollY || el.scrollTop || 0 : el.scrollTop || 0;

const writeScroll = (el, top) => {
  if (!el) return;
  el.scrollTop = top;
  if (isDocScroller(el)) window.scrollTo(0, top);
};

// forwardRef so the parent tabbar (MainTabs) can trigger a refresh from its
// inline icon — saves the toolbar row inside the 240px iframe.
const CallHistory = forwardRef(({ active }, ref) => {
  const authCtx = useContext(AuthContext);
  const ticket = useContext(TicketContext);
  const { isLoading, sendRequest } = useHttp();

  const [sessions, setSessions] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  const rootRef = useRef(null);
  const scrollerRef = useRef(null);
  const lastScrolledRef = useRef(null);
  const savedScrollRef = useRef(0);

  const ticketId = ticket && ticket.id;

  // Body shape from the Hubspot reference (canonical for the new API).
  const buildBody = (offset) => ({
    filter: 1,
    skip: offset,
    limit: PAGE_SIZE,
    search: String(ticketId || ""),
    allUsers: true,
    userIds: [],
    orderBy: "descending",
    orderColumn: "CreatedAt",
  });

  const fetchSessions = useCallback(
    (reset) => {
      if (!ticketId || !authCtx.token) return;
      const offset = reset ? 0 : skip;
      setErrorMsg("");
      sendRequest(
        {
          url: "sessions/history",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + authCtx.token,
            // Both headers kept additively during the API migration.
            usertoken: authCtx.token,
          },
          body: buildBody(offset),
        },
        (data) => {
          setHasFetchedOnce(true);
          if (data && data.errorStatus) {
            setErrorMsg(data.message || "Failed to load sessions");
            return;
          }
          const items = (data && data.items) || [];
          // Defensive: server search is substring-based on ticket id, so for
          // short Zendesk ids it returns false positives. Filter by exact
          // reference match. hasMore is based on the matched count so the
          // Load more button only shows when there's a true full page of
          // matches for this ticket.
          const ticketIdStr = String(ticketId);
          const matched = items.filter(
            (s) => String(s.reference ?? s.Reference ?? "") === ticketIdStr
          );
          if (reset) {
            setSessions(matched);
            setSkip(PAGE_SIZE);
          } else {
            setSessions((prev) => [...prev, ...matched]);
            setSkip((prev) => prev + PAGE_SIZE);
          }
          setHasMore(matched.length >= PAGE_SIZE);
        }
      );
    },
    // sendRequest is stable (useHttp memoizes it with []).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticketId, authCtx.token, skip]
  );

  // Refetch every time the History subtab becomes active. Matches the old
  // ShowMe behavior — clicking the History pill always shows fresh data.
  // Panels stay mounted (WAI-ARIA pattern in MainTabs), so the effect fires
  // only when `active` transitions to true, not on every render.
  useEffect(() => {
    if (!active) return;
    if (!ticketId || !authCtx.token) return;
    fetchSessions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ticketId, authCtx.token]);

  // The element that fires a scroll event IS the scroll container — more
  // reliable than guessing via overflow style.
  useEffect(() => {
    const onScroll = (e) => {
      const t = e.target;
      if (t === document) {
        lastScrolledRef.current = document.scrollingElement || document.documentElement;
      } else if (t && t.nodeType === 1) {
        lastScrolledRef.current = t;
      }
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, { capture: true });
  }, []);

  const resolveScroller = () =>
    lastScrolledRef.current || findScroller(rootRef.current);

  // Open detail → snap to top; back → restore saved position. raf waits one
  // paint so the new layout has its real height before scrollTop is written.
  useEffect(() => {
    const sc = resolveScroller();
    scrollerRef.current = sc;
    const raf = requestAnimationFrame(() => {
      if (selectedSession) writeScroll(sc, 0);
      else writeScroll(sc, savedScrollRef.current || 0);
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedSession]);

  const openSession = (s) => {
    const sc = resolveScroller();
    scrollerRef.current = sc;
    savedScrollRef.current = readScroll(sc);
    setSelectedSession(s);
  };

  const backToList = () => setSelectedSession(null);

  const onRefresh = useCallback(() => {
    // setSelectedSession(null) is a no-op when already on the list, so the
    // [selectedSession] effect won't fire — scroll and clear explicitly.
    savedScrollRef.current = 0;
    const sc = resolveScroller();
    scrollerRef.current = sc;
    requestAnimationFrame(() => writeScroll(sc, 0));
    setSelectedSession(null);
    setSessions([]);
    setHasMore(false);
    fetchSessions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSessions]);

  // Expose refresh + isLoading to MainTabs so it can render the icon in the
  // tabbar instead of us owning a sticky toolbar row.
  useImperativeHandle(
    ref,
    () => ({ refresh: onRefresh, isLoading }),
    [onRefresh, isLoading]
  );

  if (selectedSession) {
    return (
      <div ref={rootRef}>
        <SessionAccordion session={selectedSession} onBack={backToList} />
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      {errorMsg && <div className="ch-error">{errorMsg}</div>}

      <div className="ch-list">
        {isLoading && sessions.length === 0 && (
          <div className="loading-state">
            <div className="spinner" />
            <div>Loading sessions…</div>
          </div>
        )}

        {!isLoading && hasFetchedOnce && sessions.length === 0 && !errorMsg && (
          <div className="ch-empty">
            <div className="ch-empty-title">No sessions found</div>
            <div className="ch-empty-sub">
              No Blitzz sessions have been created for this ticket yet.
            </div>
          </div>
        )}

        {!isLoading && !hasFetchedOnce && !ticketId && !errorMsg && (
          <div className="ch-empty">
            <div className="ch-empty-sub">Waiting for ticket context…</div>
          </div>
        )}

        {sessions.map((s) => {
          const reference = s.reference ?? s.Reference ?? "";
          const code = s.code || s.sessionShortId || s.sessionId;
          const created = s.createdAt || s.createDateTime || s.CreatedAt;
          const duration = s.duration ?? s.callDuration;
          return (
            <div
              key={s.sessionId || code}
              className="session-card"
              onClick={() => openSession(s)}
            >
              <div className="sc-line">
                <div>
                  <span className="lbl">Reference:</span> {reference}
                </div>
                <div>
                  <span className="lbl">Code:</span> {code}
                </div>
              </div>
              <div className="sc-footer">
                <div>
                  <span className="lbl">Duration:</span> {formatDuration(duration)}
                </div>
                <div>
                  <span className="lbl">Created:</span> {formatShortDate(created)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {hasMore && !isLoading && sessions.length > 0 && (
        <div className="ch-load-more">
          <button
            type="button"
            className="ch-refresh-btn"
            onClick={() => fetchSessions(false)}
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
});

CallHistory.displayName = "CallHistory";

export default CallHistory;
